/** Unified profile tab: identity + status + extended fields + social links. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  UploadSimple,
  Plus,
  Trash,
  XLogo,
  InstagramLogo,
  TiktokLogo,
  Heart,
  X,
} from "@phosphor-icons/react";
import { modelsAdminApi, MODEL_STATUSES } from "../../../api/models";

const FIELD = "rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white w-full";
const LABEL =
  "block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5";

const SOCIAL_DEFS = [
  { platform: "onlyfans", Icon: Heart, label: "OnlyFans" },
  { platform: "x", Icon: XLogo, label: "X" },
  { platform: "instagram", Icon: InstagramLogo, label: "Instagram" },
  { platform: "tiktok", Icon: TiktokLogo, label: "TikTok" },
];

function SocialEditor({ socials, onChange }) {
  const { t } = useTranslation();
  const grouped = useMemo(() => {
    const m = { onlyfans: [], x: [], instagram: [], tiktok: [] };
    (socials || []).forEach((s) => {
      if (m[s.platform]) m[s.platform].push(s);
    });
    return m;
  }, [socials]);

  const flatten = (next) =>
    onChange(
      ["onlyfans", "x", "instagram", "tiktok"].flatMap(
        (p) => next[p] || [],
      ),
    );

  const add = (platform) => {
    const next = { ...grouped };
    next[platform] = [...(next[platform] || []), { platform, handle: "", url: "" }];
    flatten(next);
  };

  const removeAt = (platform, idx) => {
    const next = { ...grouped };
    next[platform] = next[platform].filter((_, i) => i !== idx);
    flatten(next);
  };

  const updateAt = (platform, idx, field, value) => {
    const next = { ...grouped };
    next[platform] = next[platform].map((s, i) =>
      i === idx ? { ...s, [field]: value } : s,
    );
    flatten(next);
  };

  return (
    <div data-testid="model-socials-editor" className="space-y-4">
      {SOCIAL_DEFS.map(({ platform, Icon, label }) => (
        <div key={platform} className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icon size={16} weight="regular" className="text-slate-700" />
              <span className="text-sm font-semibold text-slate-900">{label}</span>
              <span className="text-xs text-slate-400">
                {(grouped[platform] || []).length}
              </span>
            </div>
            <button
              type="button"
              data-testid={`socials-add-${platform}`}
              onClick={() => add(platform)}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700 hover:border-slate-900"
            >
              <Plus size={12} weight="bold" />
              {t("models.socials.add")}
            </button>
          </div>
          {(grouped[platform] || []).length === 0 ? (
            <p className="text-xs text-slate-400">{t("models.socials.empty")}</p>
          ) : (
            <div className="space-y-2">
              {(grouped[platform] || []).map((s, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-6 shrink-0">@</span>
                  <input
                    data-testid={`socials-${platform}-handle-${idx}`}
                    value={s.handle || ""}
                    onChange={(e) => updateAt(platform, idx, "handle", e.target.value)}
                    placeholder={t("models.socials.handle_placeholder")}
                    className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm flex-1 min-w-0"
                  />
                  <input
                    data-testid={`socials-${platform}-url-${idx}`}
                    value={s.url || ""}
                    onChange={(e) => updateAt(platform, idx, "url", e.target.value)}
                    placeholder={t("models.socials.url_placeholder")}
                    className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm flex-1 min-w-0"
                  />
                  <button
                    type="button"
                    onClick={() => removeAt(platform, idx)}
                    aria-label={t("common.delete")}
                    className="text-slate-400 hover:text-red-600 transition-colors"
                  >
                    <Trash size={14} weight="regular" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminModelProfile() {
  const { t } = useTranslation();
  const { modelId, model, reload } = useOutletContext();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  useEffect(() => {
    if (!model) return;
    setForm({
      status: model.status || "prospect",
      first_name: model.first_name || "",
      last_name: model.last_name || "",
      email: model.email || "",
      handle: model.handle || "",
      phone: model.phone || "",
      birth_date: model.birth_date || "",
      nationality: model.nationality || "",
      address: model.address || "",
      iban: model.iban || "",
      vat_number: model.vat_number || "",
      legal_status: model.legal_status || "unknown",
      joined_at: model.joined_at || "",
      socials: model.socials || [],
    });
  }, [model]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useCallback(async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Empty strings → drop, except status/socials always sent
      Object.keys(payload).forEach((k) => {
        if (k === "status" || k === "socials") return;
        if (payload[k] === "" || payload[k] === undefined) delete payload[k];
      });
      // Strip empty socials
      payload.socials = (payload.socials || []).filter((s) =>
        (s.handle || "").trim(),
      );
      await modelsAdminApi.update(modelId, payload);
      setSavedAt(new Date());
      reload?.();
    } finally {
      setSaving(false);
    }
  }, [form, modelId, reload]);

  const onPhotoChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      setPhotoBusy(true);
      try {
        await modelsAdminApi.uploadPhoto(modelId, file);
        reload?.();
      } finally {
        setPhotoBusy(false);
      }
    },
    [modelId, reload],
  );

  const onRemovePhoto = useCallback(async () => {
    if (!window.confirm(t("models.photo.remove_confirm"))) return;
    setPhotoBusy(true);
    try {
      await modelsAdminApi.removePhoto(modelId);
      reload?.();
    } finally {
      setPhotoBusy(false);
    }
  }, [modelId, reload, t]);

  if (!model || !form)
    return <p className="text-sm text-slate-500">{t("common.loading")}</p>;

  const photoUrl = model.photo_id
    ? modelsAdminApi.photoUrl(model.photo_id)
    : null;

  return (
    <div data-testid="model-profile-tab" className="space-y-6 max-w-3xl">
      {/* Photo controls + clickable thumbnail */}
      <div className="flex items-center gap-4">
        {photoUrl ? (
          <button
            type="button"
            data-testid="profile-photo-thumb"
            onClick={() => setLightboxOpen(true)}
            className="relative h-20 w-20 shrink-0 rounded-2xl overflow-hidden border border-slate-200 group/photo focus:outline-none focus:ring-2 focus:ring-slate-900"
            aria-label={t("models.photo.view")}
          >
            <img
              src={photoUrl}
              alt=""
              className="h-full w-full object-cover transition-transform duration-300 group-hover/photo:scale-105"
            />
            <span className="absolute inset-0 bg-slate-900/0 group-hover/photo:bg-slate-900/30 transition-colors" />
          </button>
        ) : (
          <span className="inline-flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 text-xs">
            {t("models.photo.empty")}
          </span>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 rounded-full border border-dashed border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:border-slate-900 cursor-pointer">
            <UploadSimple size={14} weight="bold" />
            {photoUrl
              ? t("models.photo.replace")
              : t("models.upload_photo")}
            <input
              type="file"
              accept="image/*"
              data-testid="model-photo-input"
              onChange={onPhotoChange}
              disabled={photoBusy}
              className="hidden"
            />
          </label>
          {photoUrl && (
            <button
              type="button"
              data-testid="profile-photo-remove"
              onClick={onRemovePhoto}
              disabled={photoBusy}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
            >
              <Trash size={13} weight="regular" />
              {t("models.photo.remove")}
            </button>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && photoUrl && (
        <div
          data-testid="profile-photo-lightbox"
          role="dialog"
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-[120] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-6"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxOpen(false);
            }}
            aria-label={t("common.cancel")}
            data-testid="profile-photo-lightbox-close"
            className="absolute top-6 right-6 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <X size={18} weight="bold" />
          </button>
          <img
            src={photoUrl}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] rounded-2xl shadow-2xl object-contain"
          />
        </div>
      )}

      {/* Status */}
      <div>
        <label className={LABEL}>{t("models.profile.status")}</label>
        <select
          data-testid="profile-status"
          className={FIELD}
          value={form.status}
          onChange={(e) => setField("status", e.target.value)}
        >
          {MODEL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`models.status.${s}`)}
            </option>
          ))}
        </select>
      </div>

      {/* Identity */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL}>{t("models.field.first_name")}</label>
          <input
            className={FIELD}
            value={form.first_name}
            onChange={(e) => setField("first_name", e.target.value)}
          />
        </div>
        <div>
          <label className={LABEL}>{t("models.field.last_name")}</label>
          <input
            className={FIELD}
            value={form.last_name}
            onChange={(e) => setField("last_name", e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL}>{t("models.field.email")}</label>
          <input
            className={FIELD}
            type="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
          />
        </div>
        <div>
          <label className={LABEL}>{t("models.field.handle")}</label>
          <input
            className={FIELD}
            value={form.handle}
            onChange={(e) => setField("handle", e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL}>{t("models.profile.phone")}</label>
          <input
            className={FIELD}
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
          />
        </div>
        <div>
          <label className={LABEL}>{t("models.profile.nationality")}</label>
          <input
            className={FIELD}
            value={form.nationality}
            onChange={(e) => setField("nationality", e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL}>{t("models.profile.birth_date")}</label>
          <input
            className={FIELD}
            type="date"
            value={form.birth_date}
            onChange={(e) => setField("birth_date", e.target.value)}
          />
        </div>
        <div>
          <label className={LABEL}>{t("models.profile.joined_at")}</label>
          <input
            className={FIELD}
            type="date"
            value={form.joined_at}
            onChange={(e) => setField("joined_at", e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className={LABEL}>{t("models.profile.address")}</label>
        <textarea
          rows={2}
          className={FIELD + " resize-y"}
          value={form.address}
          onChange={(e) => setField("address", e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL}>{t("models.profile.iban")}</label>
          <input
            className={FIELD}
            value={form.iban}
            onChange={(e) => setField("iban", e.target.value)}
          />
        </div>
        <div>
          <label className={LABEL}>{t("models.profile.vat_number")}</label>
          <input
            className={FIELD}
            data-testid="profile-vat-number"
            placeholder="FR12345678901"
            value={form.vat_number}
            onChange={(e) =>
              setField("vat_number", e.target.value.toUpperCase().replace(/\s/g, ""))
            }
          />
          {form.vat_number &&
            (() => {
              const EU = [
                "AT","BE","BG","CY","CZ","DE","DK","EE","EL","GR","ES","FI",
                "FR","HR","HU","IE","IT","LT","LU","LV","MT","NL","PL","PT",
                "RO","SE","SI","SK","XI",
              ];
              const prefix = form.vat_number.slice(0, 2);
              const body = form.vat_number.slice(2);
              const ok =
                EU.includes(prefix) && /^[A-Z0-9]{2,12}$/.test(body);
              return !ok ? (
                <p
                  data-testid="profile-vat-warning"
                  className="mt-1 text-[11px] text-amber-600"
                >
                  {t("models.profile.vat_warning")}
                </p>
              ) : null;
            })()}
        </div>
      </div>

      <div>
        <label className={LABEL}>{t("models.profile.legal_status")}</label>
        <select
          className={FIELD}
          value={form.legal_status}
          onChange={(e) => setField("legal_status", e.target.value)}
        >
          <option value="unknown">—</option>
          <option value="auto_entrepreneur">Auto-entrepreneur</option>
          <option value="company">Société</option>
          <option value="employee">Salarié</option>
          <option value="other">Autre</option>
        </select>
      </div>

      {/* Social links */}
      <div className="pt-2">
        <p className={LABEL}>{t("models.socials.title")}</p>
        <SocialEditor
          socials={form.socials}
          onChange={(socials) => setField("socials", socials)}
        />
      </div>

      <div className="flex justify-end items-center gap-3 pt-2">
        {savedAt && (
          <span className="text-xs text-emerald-600">
            {t("admin.courses.saved_at", {
              time: savedAt.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            })}
          </span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="model-profile-save"
          className="rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {saving ? t("admin.courses.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}
